import type { MutableRefObject } from 'react';
import type { DragItem } from 'react-aria';

import type { ContentRef } from '@gitbook/api-client';
import type { DragAndDropOptions, TreeData } from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

type MaybeLinkItem = {
    title: string;
    to?: ContentRef | null;
};

/**
 * Returns the getItems function for a DragAndDropOptions object.
 */
export function getItemsFactory<T extends MaybeLinkItem>(
    treeRef: MutableRefObject<TreeData<T>>
): NonNullable<DragAndDropOptions['getItems']> {
    return (keys) => {
        return Array.from(keys).map((key) => {
            const item = treeRef.current.getItem(key);
            assert(item);
            const data: DragItem = {
                'x-gitbook/item-key': String(item.key),
                'text/plain': item.value.title,
            };
            if ('to' in item.value && item.value.to && item.value.to.kind === 'url') {
                data['text/uri-list'] = item.value.to.url;
            }
            return data;
        });
    };
}

/**
 * Check if a link will be broken in GBO.
 * @param item The item to check
 * @returns True if the link does not point to a space belonging to the current site, false otherwise.
 */
export function isLinkBroken(item: MaybeLinkItem, siteSpaceIds: string[]): boolean {
    return Boolean(item.to && isContentRefBroken(item.to, siteSpaceIds));
}

/**
 * Check if a content reference is broken.
 * @param ref The content reference to check
 * @param siteSpaceIds The list of space IDs belonging to the current site
 * @returns True if the content reference is broken, false otherwise.
 */
export function isContentRefBroken(
    ref: ContentRef | null | undefined,
    siteSpaceIds: string[]
): boolean {
    return Boolean(ref && ref.kind === 'page' && ref.space && !siteSpaceIds.includes(ref.space));
}
